/**
 * Centralized query key factory for React Query
 *
 * This ensures consistent cache keys across the application and enables
 * targeted cache invalidation.
 *
 * Pattern: [scope, entity, ...params]
 * Example: ['school', 'assignments', { schoolId, familyId }]
 */

export const queryKeys = {
  // School-scoped queries
  school: {
    all: (schoolId: string) => ['school', schoolId] as const,

    // Assignments
    assignments: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'assignments'] as const,
      byFamily: (schoolId: string, familyId: string) =>
        [...queryKeys.school.assignments.all(schoolId), { familyId }] as const,
      byChild: (schoolId: string, childId: string) =>
        [...queryKeys.school.assignments.all(schoolId), { childId }] as const,
      byStatus: (schoolId: string, status: string) =>
        [...queryKeys.school.assignments.all(schoolId), { status }] as const,
      single: (schoolId: string, assignmentId: string) =>
        [...queryKeys.school.assignments.all(schoolId), assignmentId] as const,
    },

    // Children
    children: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'children'] as const,
      byFamily: (schoolId: string, familyId: string) =>
        [...queryKeys.school.children.all(schoolId), { familyId }] as const,
      single: (schoolId: string, childId: string) =>
        [...queryKeys.school.children.all(schoolId), childId] as const,
    },

    // Families
    families: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'families'] as const,
      single: (schoolId: string, familyId: string) =>
        [...queryKeys.school.families.all(schoolId), familyId] as const,
    },

    // Declarations
    declarations: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'declarations'] as const,
      active: (schoolId: string) =>
        [...queryKeys.school.declarations.all(schoolId), { status: 'active' }] as const,
      single: (schoolId: string, declarationId: string) =>
        [...queryKeys.school.declarations.all(schoolId), declarationId] as const,
    },

    // Signatures
    signatures: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'signatures'] as const,
      byAssignment: (schoolId: string, assignmentId: string) =>
        [...queryKeys.school.signatures.all(schoolId), { assignmentId }] as const,
      single: (schoolId: string, signatureId: string) =>
        [...queryKeys.school.signatures.all(schoolId), signatureId] as const,
    },

    // Invitation Tokens
    invitationTokens: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'invitationTokens'] as const,
      byFamily: (schoolId: string, familyId: string) =>
        [...queryKeys.school.invitationTokens.all(schoolId), { familyId }] as const,
    },

    // Email Templates
    emailTemplates: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'emailTemplates'] as const,
      single: (schoolId: string, templateId: string) =>
        [...queryKeys.school.emailTemplates.all(schoolId), templateId] as const,
    },

    // Teachers
    teachers: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'teachers'] as const,
      single: (schoolId: string, teacherId: string) =>
        [...queryKeys.school.teachers.all(schoolId), teacherId] as const,
    },

    // Classes
    classes: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'classes'] as const,
      single: (schoolId: string, classId: string) =>
        [...queryKeys.school.classes.all(schoolId), classId] as const,
    },

    // Schedule Slots
    scheduleSlots: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'scheduleSlots'] as const,
      single: (schoolId: string, slotId: string) =>
        [...queryKeys.school.scheduleSlots.all(schoolId), slotId] as const,
    },

    // Family Fees
    familyFees: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'familyFees'] as const,
      byFamily: (schoolId: string, familyId: string) =>
        [...queryKeys.school.all(schoolId), 'familyFees', { familyId }] as const,
    },

    // Teacher View
    teacherDashboard: (schoolId: string) => [...queryKeys.school.all(schoolId), 'teacherDashboard'] as const,
    classChildren: (schoolId: string, classId: string) => [...queryKeys.school.all(schoolId), 'classChildren', classId] as const,

    // Expenses
    expenses: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'expenses'] as const,
      filtered: (schoolId: string, filters?: any) =>
        [...queryKeys.school.expenses.all(schoolId), { filters }] as const,
      statistics: (schoolId: string) =>
        [...queryKeys.school.expenses.all(schoolId), 'statistics'] as const,
    },

    // Advances
    advances: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'advances'] as const,
      filtered: (schoolId: string, filters?: any) =>
        [...queryKeys.school.advances.all(schoolId), { filters }] as const,
      details: (schoolId: string, advanceId: string) =>
        [...queryKeys.school.advances.all(schoolId), advanceId] as const,
      recipientBalance: (schoolId: string, recipientId: string) =>
        [...queryKeys.school.advances.all(schoolId), 'balance', recipientId] as const,
    },

    // Recipients
    recipients: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'recipients'] as const,
      filtered: (schoolId: string, filters?: any) =>
        [...queryKeys.school.recipients.all(schoolId), { filters }] as const,
    },

    // Volunteers
    volunteers: {
      all: (schoolId: string) => [...queryKeys.school.all(schoolId), 'volunteers'] as const,
      byFamily: (schoolId: string, familyId: string) =>
        [...queryKeys.school.all(schoolId), 'volunteers', { familyId }] as const,
    },

    // Reports
    reports: (schoolId: string, filters?: any) => ['school', schoolId, 'reports', filters] as const,

    // Report Statistics (fee collection, enrollment timeline)
    reportStatistics: (schoolId: string) => ['school', schoolId, 'reportStatistics'] as const,

    // Teacher Workload
    teacherWorkload: (schoolId: string) => ['school', schoolId, 'teacherWorkload'] as const,
  },

  // User-scoped queries
  user: {
    profile: (userId: string) => ['user', userId, 'profile'] as const,
  },
} as const;

/**
 * Type helper to extract query key type
 */
export type QueryKey = ReturnType<
  | typeof queryKeys.school.assignments.all
  | typeof queryKeys.school.assignments.byFamily
  | typeof queryKeys.school.children.all
  | typeof queryKeys.school.children.byFamily
  | typeof queryKeys.school.families.all
  | typeof queryKeys.school.declarations.all
>;
